import AdCards from "@/components/ad-cards";
import AdStrategy from "@/components/ad-strategy";
import AdsLaunchedChart from "@/components/ads-launched-chart";
import MediaMix from "@/components/media-mix";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { AdLibraryResponse, Brand } from "@/hooks/api/use-brand-ads";
import { AlertCircle, ExternalLink } from "lucide-react";

interface AdResultsViewProps {
  brand: Brand;
  competitorAds: AdLibraryResponse;
  onClose?: () => void;
  isLoading?: boolean;
}

// AdResultsView Skeleton component
function AdResultsViewSkeleton() {
  return (
    <Card className="p-4 sm:p-6">
      <div className="space-y-8">
        {/* Header skeleton */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Skeleton className="w-16 h-16 rounded-full" />
            <div>
              <Skeleton className="h-6 w-40 mb-2" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <Skeleton className="h-10 w-56" />
        </div>

        {/* Charts skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-[200px] w-full rounded-lg" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-[200px] w-full rounded-lg" />
          </div>
        </div>

        {/* Accordion skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>

        <Separator />

        {/* Ad library skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-6 w-32" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <Skeleton key={i} className="h-[300px] w-full rounded-lg" />
              ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function AdResultsView({
  brand,
  competitorAds,
  onClose,
  isLoading = false,
}: AdResultsViewProps) {
  // Show skeleton when loading
  if (isLoading) {
    return <AdResultsViewSkeleton />;
  }

  const { results, overview, created_at, strategy_evolution, structured_data } =
    competitorAds;

  // Show empty state when no ads are available
  if (!results || results.length === 0) {
    return (
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <a
              href={`https://www.facebook.com/${brand.facebook_page_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                {brand.profile_photo ? (
                  <img
                    src={brand.profile_photo}
                    alt={`${brand.name} profile`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.removeAttribute(
                        "style"
                      );
                    }}
                  />
                ) : (
                  <span
                    className="text-2xl font-semibold"
                    style={{
                      display: "block",
                    }}
                  >
                    {brand.name?.[0]?.toUpperCase() || ""}
                  </span>
                )}
              </div>
            </a>
            <div>
              <div className="flex items-center gap-2">
                <a
                  href={`https://www.facebook.com/${brand.facebook_page_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-1 transition-colors"
                >
                  <h3 className="text-xl font-bold">{brand.name}</h3>
                  <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                {onClose && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="ml-2"
                  >
                    &times;
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{brand.about}</p>
            </div>
          </div>
          <Button variant="outline" className="flex items-center gap-2" asChild>
            <a
              href={`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=US&view_all_page_id=${brand.facebook_page_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Facebook Ad Library
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>

        <EmptyState
          title="No Ads Available"
          description={`We couldn't find any active ads for ${brand.name}. This could be because the brand isn't currently running ads or the ads haven't been indexed yet.`}
          icons={[AlertCircle]}
          className="py-12"
        />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <a
              href={`https://www.facebook.com/${brand.facebook_page_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                {brand.profile_photo ? (
                  <img
                    src={brand.profile_photo}
                    alt={`${brand.name} profile`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.removeAttribute(
                        "style"
                      );
                    }}
                  />
                ) : (
                  <span
                    className="text-2xl font-semibold"
                    style={{
                      display: "block",
                    }}
                  >
                    {brand.name?.[0]?.toUpperCase() || ""}
                  </span>
                )}
              </div>
            </a>
            <div>
              <div className="flex items-center gap-2">
                <a
                  href={`https://www.facebook.com/${brand.facebook_page_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-1 transition-colors"
                >
                  <h3 className="text-xl font-bold">{brand.name}</h3>
                  <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                {onClose && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="ml-2"
                  >
                    &times;
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{brand.about}</p>
            </div>
          </div>
          <Button variant="outline" className="flex items-center gap-2" asChild>
            <a
              href={`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=US&view_all_page_id=${brand.facebook_page_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Facebook Ad Library
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <MediaMix ads={results} />
          <AdsLaunchedChart ads={results} />
        </div>

        <AdStrategy
          overview={overview}
          strategyEvolution={strategy_evolution}
          createdAt={created_at}
          ads={results}
          structuredData={structured_data}
        />

        <Separator />

        <AdCards results={results} brand={brand} />
      </div>
    </Card>
  );
}
